import {
  normalizeCustomerAgreements,
  normalizeCustomerSearchInput,
  type AddCustomerAttachmentInput,
  type CheckCustomerDuplicatesInput,
  type CreateCustomerAddressInput,
  type CustomerAttachmentRecord,
  type CreateCustomerContactInput,
  type CreateCustomerInput,
  type CustomerAddressKind,
  type CustomerDetailRecord,
  type CustomerDuplicateCandidate,
  type CustomerDuplicateReason,
  type CustomerListRecord,
  type CustomerPage,
  type CustomerSearchInput,
  type CustomersGateway,
  type UpdateCustomerAddressInput,
  type UpdateCustomerAgreementsInput,
  type UpdateCustomerContactInput,
  type UpdateCustomerInput,
} from '@forq/customers-data';

function clone<T>(value: T): T {
  return structuredClone(value);
}

function prefixFor(tenantId: string): '1' | '2' | '3' {
  if (tenantId === 'stw' || tenantId.startsWith('2222')) return '2';
  if (tenantId === 'gsl' || tenantId.startsWith('3333')) return '3';
  return '1';
}

function clean(value: string | undefined): string {
  return value?.trim() ?? '';
}

function matchText(value: string | undefined): string {
  return clean(value).toLocaleLowerCase('de').replace(/[^\p{L}\p{N}]+/gu, '');
}

function phoneDigits(value: string | undefined): string {
  return clean(value).replace(/[^0-9]+/g, '');
}

function customerName(input: Pick<CreateCustomerInput, 'kind' | 'companyName' | 'firstName' | 'lastName'>): string {
  if (input.kind === 'company') {
    const companyName = clean(input.companyName);
    if (!companyName) throw new Error('Für Firmenkunden ist ein Firmenname erforderlich.');
    return companyName;
  }
  const personName = [clean(input.firstName), clean(input.lastName)].filter(Boolean).join(' ');
  if (!personName) throw new Error('Für Privatkunden ist ein Name erforderlich.');
  return personName;
}

function preferredCity(customer: CustomerDetailRecord): string {
  const address = customer.addresses.find((item) => item.kind === 'billing' && item.primary)
    ?? customer.addresses.find((item) => item.primary)
    ?? customer.addresses[0];
  return address?.city ?? '';
}

function withDerived(customer: CustomerDetailRecord): CustomerDetailRecord {
  return {
    ...customer,
    city: preferredCity(customer),
    contactCount: customer.contacts.filter((contact) => contact.active).length,
  };
}

function toList(customer: CustomerDetailRecord): CustomerListRecord {
  const derived = withDerived(customer);
  return {
    id: derived.id,
    number: derived.number,
    displayName: derived.displayName,
    city: derived.city,
    status: derived.status,
    phone: derived.phone,
    email: derived.email,
    vatId: derived.vatId,
    contactCount: derived.contactCount,
    updatedAt: derived.updatedAt,
    activeSince: derived.activeSince,
    activeUntil: derived.activeUntil,
  };
}

function addressLabel(kind: CustomerAddressKind): string {
  if (kind === 'delivery') return 'Lieferanschrift';
  if (kind === 'site') return 'Einsatzort';
  return 'Rechnungsanschrift';
}

function createTenantCustomers(tenantId: string): CustomerDetailRecord[] {
  const prefix = prefixFor(tenantId);
  const now = new Date().toISOString();
  const companyNames = prefix === '2'
    ? ['Westfalen Schwerlast GmbH', 'Meyer Maschinenlogistik KG', 'Spedition Hansa GmbH']
    : prefix === '3'
      ? ['Sauerland Holztechnik GmbH', 'Metallbau Arnsberg KG', 'Winterberg Logistik GmbH']
      : ['Werl Industriebedarf GmbH', 'Bäckerei Haverkamp GmbH', 'Schulte Baustoffe KG'];

  return companyNames.map((name, index) => withDerived({
    id: `${tenantId}-customer-${index + 1}`,
    tenantId,
    number: `${prefix}${String(index + 1).padStart(4, '0')}`,
    kind: 'company',
    displayName: name,
    companyName: name,
    firstName: '',
    lastName: '',
    city: '',
    status: index === 2 ? 'prospect' : 'active',
    phone: `02922 90${index + 1}00`,
    email: `info${index + 1}@example.test`,
    vatId: `DE12345${prefix}${index + 1}`,
    taxNumber: '',
    website: '',
    notes: index === 0 ? 'Demokunde für das neue Kundenmodul.' : '',
    addresses: [{
      id: `${tenantId}-address-${index + 1}`,
      kind: 'billing',
      label: 'Rechnungsanschrift',
      street: `Industriestraße ${index + 1}`,
      postalCode: index === 0 ? '59457' : index === 1 ? '59494' : '59755',
      city: index === 0 ? 'Werl' : index === 1 ? 'Soest' : 'Arnsberg',
      countryCode: 'DE',
      primary: true,
    }],
    contacts: [{
      id: `${tenantId}-contact-${index + 1}`,
      salutation: index % 2 === 0 ? 'Herr' : 'Frau',
      firstName: index % 2 === 0 ? 'Max' : 'Anna',
      lastName: `Kontakt ${index + 1}`,
      position: 'Einkauf',
      email: `kontakt${index + 1}@example.test`,
      phone: `02922 80${index + 1}00`,
      mobile: '',
      primary: true,
      active: true,
    }],
    agreements: index === 0
      ? { debtorNumber: `${prefix}0001`, leitwegId: '', dunningBlocked: false, dunningBlockReason: '', creditLimit: 25000, discountPercent: 5, paymentTermDays: 30, conditions: 'Rahmenvereinbarung 2026: 5 % auf Ersatzteile, Fracht frei ab 500 €.', deliveryTerms: 'DAP – frei Haus', bankName: 'Sparkasse HellwegLippe', iban: 'DE12 4145 0075 0001 2345 67', bic: 'WELADED1SOS', specialParts: [{ id: `${tenantId}-part-1`, partNumber: 'GT-4512', description: 'Gabelzinken 1200 mm (Sonderlänge)', note: 'Kundenspezifisch, nur auf Bestellung.' }] }
      : { debtorNumber: '', leitwegId: '', dunningBlocked: false, dunningBlockReason: '', creditLimit: 0, discountPercent: 0, paymentTermDays: 30, conditions: '', deliveryTerms: '', bankName: '', iban: '', bic: '', specialParts: [] },
    contactCount: 1,
    createdAt: now,
    updatedAt: now,
    activeSince: ['2021-03-01', '2023-06-15', '2025-01-10'][index] ?? now.slice(0, 10),
    activeUntil: null,
  }));
}

export class DemoCustomersGateway implements CustomersGateway {
  private readonly customers = new Map<string, CustomerDetailRecord[]>();
  /** Kundenakte-Anhänge je `${tenantId}:${customerId}`. In Supabase eine eigene Tabelle. */
  private readonly attachments = new Map<string, CustomerAttachmentRecord[]>();

  private state(tenantId: string): CustomerDetailRecord[] {
    const existing = this.customers.get(tenantId);
    if (existing) return existing;
    const created = createTenantCustomers(tenantId);
    this.customers.set(tenantId, created);
    return created;
  }

  private locate(tenantId: string, customerId: string): {
    customers: CustomerDetailRecord[];
    index: number;
    customer: CustomerDetailRecord;
  } {
    const customers = this.state(tenantId);
    const index = customers.findIndex((item) => item.id === customerId);
    if (index < 0) throw new Error('Kunde wurde in diesem Mandanten nicht gefunden.');
    return { customers, index, customer: customers[index] };
  }

  private replace(tenantId: string, customerId: string, customer: CustomerDetailRecord): CustomerDetailRecord {
    const located = this.locate(tenantId, customerId);
    const next = withDerived({ ...customer, updatedAt: new Date().toISOString() });
    located.customers[located.index] = next;
    return clone(next);
  }

  async searchCustomers(input: CustomerSearchInput): Promise<CustomerPage> {
    const normalized = normalizeCustomerSearchInput(input);
    const query = normalized.query.toLocaleLowerCase('de');
    const filtered = this.state(normalized.tenantId)
      .map(withDerived)
      .filter((customer) => normalized.status === 'all' || customer.status === normalized.status)
      .filter((customer) => !query || [
        customer.number,
        customer.displayName,
        customer.city,
        customer.vatId,
        customer.email,
      ].some((item) => item.toLocaleLowerCase('de').includes(query)))
      .filter((customer) => {
        if (!normalized.bestandFrom && !normalized.bestandTo) return true;
        const from = normalized.bestandFrom || '0000-01-01';
        const to = normalized.bestandTo || '9999-12-31';
        // Im Bestand während [from, to]: Zugang ≤ to UND (noch aktiv ODER Abgang ≥ from).
        return customer.activeSince <= to && (customer.activeUntil === null || customer.activeUntil >= from);
      })
      .sort((left, right) => left.displayName.localeCompare(right.displayName, 'de'));
    const start = (normalized.page - 1) * normalized.pageSize;
    return {
      items: clone(filtered.slice(start, start + normalized.pageSize).map(toList)),
      total: filtered.length,
      page: normalized.page,
      pageSize: normalized.pageSize,
    };
  }

  async getCustomer(tenantId: string, customerId: string): Promise<CustomerDetailRecord> {
    return clone(withDerived(this.locate(tenantId, customerId).customer));
  }

  async findDuplicates(input: CheckCustomerDuplicatesInput): Promise<readonly CustomerDuplicateCandidate[]> {
    const inputName = matchText(input.kind === 'company'
      ? input.companyName
      : [input.firstName, input.lastName].filter(Boolean).join(' '));
    const inputVat = matchText(input.vatId);
    const inputEmail = clean(input.email).toLocaleLowerCase('de');
    const inputPhone = phoneDigits(input.phone);
    const inputPostal = matchText(input.postalCode);
    const inputCity = matchText(input.city);

    return this.state(input.tenantId)
      .filter((customer) => customer.id !== input.customerId)
      .map((customer) => {
        const reasons: CustomerDuplicateReason[] = [];
        let score = 0;
        const address = customer.addresses.find((item) => item.kind === 'billing' && item.primary)
          ?? customer.addresses.find((item) => item.primary)
          ?? customer.addresses[0];
        const candidateName = matchText(customer.displayName);
        if (inputVat && inputVat === matchText(customer.vatId)) {
          reasons.push('vat-id');
          score = Math.max(score, 100);
        }
        if (inputEmail && inputEmail === clean(customer.email).toLocaleLowerCase('de')) {
          reasons.push('email');
          score = Math.max(score, 90);
        }
        if (inputPhone.length >= 6 && inputPhone === phoneDigits(customer.phone)) {
          reasons.push('phone');
          score = Math.max(score, 80);
        }
        if (inputName && inputName === candidateName) {
          if (inputPostal && inputPostal === matchText(address?.postalCode)) {
            reasons.push('name-postal-code');
            score = Math.max(score, 75);
          } else if (inputCity && inputCity === matchText(address?.city)) {
            reasons.push('name-city');
            score = Math.max(score, 65);
          } else {
            reasons.push('name');
            score = Math.max(score, 50);
          }
        }
        return { customer, score, reasons };
      })
      .filter((candidate) => candidate.score >= 50)
      .sort((left, right) => right.score - left.score || left.customer.displayName.localeCompare(right.customer.displayName, 'de'))
      .map(({ customer, score, reasons }) => ({
        id: customer.id,
        number: customer.number,
        displayName: customer.displayName,
        city: preferredCity(customer),
        status: customer.status,
        score,
        reasons,
      }));
  }

  async createCustomer(input: CreateCustomerInput): Promise<CustomerDetailRecord> {
    const customers = this.state(input.tenantId);
    const vatId = clean(input.vatId).toUpperCase();
    if (vatId && customers.some((customer) => customer.vatId.toUpperCase() === vatId)) {
      throw new Error('Die USt-ID ist diesem Mandanten bereits zugeordnet.');
    }
    const prefix = prefixFor(input.tenantId);
    const nextCounter = customers.reduce((highest, customer) => (
      customer.number.startsWith(prefix)
        ? Math.max(highest, Number(customer.number.slice(1)) || 0)
        : highest
    ), 0) + 1;
    const name = customerName(input);
    const now = new Date().toISOString();
    const id = `${input.tenantId}-customer-${crypto.randomUUID()}`;
    const address = input.billingAddress?.city ? [{
      id: `${id}-address-${crypto.randomUUID()}`,
      kind: 'billing' as const,
      label: clean(input.billingAddress.label) || 'Rechnungsanschrift',
      street: clean(input.billingAddress.street),
      postalCode: clean(input.billingAddress.postalCode),
      city: clean(input.billingAddress.city),
      countryCode: clean(input.billingAddress.countryCode).toUpperCase() || 'DE',
      primary: true,
    }] : [];
    const contact = input.primaryContact?.lastName ? [{
      id: `${id}-contact-${crypto.randomUUID()}`,
      salutation: clean(input.primaryContact.salutation),
      firstName: clean(input.primaryContact.firstName),
      lastName: clean(input.primaryContact.lastName),
      position: clean(input.primaryContact.position),
      email: clean(input.primaryContact.email),
      phone: clean(input.primaryContact.phone),
      mobile: clean(input.primaryContact.mobile),
      primary: true,
      active: true,
    }] : [];
    const created = withDerived({
      id,
      tenantId: input.tenantId,
      number: `${prefix}${String(nextCounter).padStart(4, '0')}`,
      kind: input.kind,
      displayName: name,
      companyName: clean(input.companyName),
      firstName: clean(input.firstName),
      lastName: clean(input.lastName),
      city: '',
      status: 'active',
      phone: clean(input.phone),
      email: clean(input.email),
      vatId,
      taxNumber: clean(input.taxNumber),
      website: clean(input.website),
      notes: clean(input.notes),
      addresses: address,
      contacts: contact,
      agreements: normalizeCustomerAgreements(input.agreements),
      contactCount: contact.length,
      createdAt: now,
      updatedAt: now,
      activeSince: now.slice(0, 10),
      activeUntil: null,
    });
    customers.push(created);
    return clone(created);
  }

  async updateCustomer(input: UpdateCustomerInput): Promise<CustomerDetailRecord> {
    const located = this.locate(input.tenantId, input.customerId);
    const vatId = clean(input.vatId).toUpperCase();
    if (vatId && located.customers.some((customer) => customer.id !== input.customerId && customer.vatId.toUpperCase() === vatId)) {
      throw new Error('Die USt-ID ist einem anderen Kunden dieses Mandanten zugeordnet.');
    }
    return this.replace(input.tenantId, input.customerId, {
      ...located.customer,
      kind: input.kind,
      displayName: customerName(input),
      companyName: clean(input.companyName),
      firstName: clean(input.firstName),
      lastName: clean(input.lastName),
      vatId,
      taxNumber: clean(input.taxNumber),
      email: clean(input.email),
      phone: clean(input.phone),
      website: clean(input.website),
      notes: clean(input.notes),
      status: input.status ?? located.customer.status,
      agreements: input.agreements ? normalizeCustomerAgreements(input.agreements) : located.customer.agreements,
    });
  }

  async createAddress(input: CreateCustomerAddressInput): Promise<CustomerDetailRecord> {
    const located = this.locate(input.tenantId, input.customerId);
    if (!clean(input.city)) throw new Error('Für eine Anschrift ist ein Ort erforderlich.');
    const sameKind = located.customer.addresses.filter((address) => address.kind === input.kind);
    const primary = Boolean(input.primary) || sameKind.length === 0;
    const addresses = [
      ...located.customer.addresses.map((address) => primary && address.kind === input.kind
        ? { ...address, primary: false }
        : address),
      {
        id: `${input.customerId}-address-${crypto.randomUUID()}`,
        kind: input.kind,
        label: clean(input.label) || addressLabel(input.kind),
        street: clean(input.street),
        postalCode: clean(input.postalCode),
        city: clean(input.city),
        countryCode: clean(input.countryCode).toUpperCase() || 'DE',
        primary,
      },
    ];
    return this.replace(input.tenantId, input.customerId, { ...located.customer, addresses });
  }

  async updateAddress(input: UpdateCustomerAddressInput): Promise<CustomerDetailRecord> {
    const located = this.locate(input.tenantId, input.customerId);
    const current = located.customer.addresses.find((address) => address.id === input.addressId);
    if (!current) throw new Error('Anschrift wurde bei diesem Kunden nicht gefunden.');
    if (!clean(input.city)) throw new Error('Für eine Anschrift ist ein Ort erforderlich.');
    const primary = Boolean(input.primary);
    let addresses = located.customer.addresses.map((address) => {
      if (address.id === input.addressId) {
        return {
          ...address,
          kind: input.kind,
          label: clean(input.label) || addressLabel(input.kind),
          street: clean(input.street),
          postalCode: clean(input.postalCode),
          city: clean(input.city),
          countryCode: clean(input.countryCode).toUpperCase() || 'DE',
          primary,
        };
      }
      return primary && address.kind === input.kind ? { ...address, primary: false } : address;
    });
    for (const kind of [current.kind, input.kind] as const) {
      if (addresses.some((address) => address.kind === kind)
        && !addresses.some((address) => address.kind === kind && address.primary)) {
        const first = addresses.find((address) => address.kind === kind);
        addresses = addresses.map((address) => address.id === first?.id
          ? { ...address, primary: true }
          : address);
      }
    }
    return this.replace(input.tenantId, input.customerId, { ...located.customer, addresses });
  }

  async deleteAddress(tenantId: string, customerId: string, addressId: string): Promise<CustomerDetailRecord> {
    const located = this.locate(tenantId, customerId);
    const deleted = located.customer.addresses.find((address) => address.id === addressId);
    if (!deleted) throw new Error('Anschrift wurde bei diesem Kunden nicht gefunden.');
    let addresses = located.customer.addresses.filter((address) => address.id !== addressId);
    if (deleted.primary && addresses.some((address) => address.kind === deleted.kind)) {
      const nextPrimary = addresses.find((address) => address.kind === deleted.kind);
      addresses = addresses.map((address) => address.id === nextPrimary?.id
        ? { ...address, primary: true }
        : address);
    }
    return this.replace(tenantId, customerId, { ...located.customer, addresses });
  }

  async createContact(input: CreateCustomerContactInput): Promise<CustomerDetailRecord> {
    const located = this.locate(input.tenantId, input.customerId);
    if (!clean(input.lastName)) throw new Error('Für den Ansprechpartner ist ein Nachname erforderlich.');
    const primary = Boolean(input.primary) || located.customer.contacts.every((contact) => !contact.active);
    const contacts = [
      ...located.customer.contacts.map((contact) => primary ? { ...contact, primary: false } : contact),
      {
        id: `${input.customerId}-contact-${crypto.randomUUID()}`,
        salutation: clean(input.salutation),
        firstName: clean(input.firstName),
        lastName: clean(input.lastName),
        position: clean(input.position),
        email: clean(input.email),
        phone: clean(input.phone),
        mobile: clean(input.mobile),
        primary,
        active: true,
      },
    ];
    return this.replace(input.tenantId, input.customerId, { ...located.customer, contacts });
  }

  async updateContact(input: UpdateCustomerContactInput): Promise<CustomerDetailRecord> {
    const located = this.locate(input.tenantId, input.customerId);
    const current = located.customer.contacts.find((contact) => contact.id === input.contactId);
    if (!current) throw new Error('Ansprechpartner wurde bei diesem Kunden nicht gefunden.');
    if (!clean(input.lastName)) throw new Error('Für den Ansprechpartner ist ein Nachname erforderlich.');
    const active = input.active ?? current.active;
    const primary = active && Boolean(input.primary);
    let contacts = located.customer.contacts
      .map((contact) => primary && contact.id !== input.contactId ? { ...contact, primary: false } : contact)
      .map((contact) => contact.id === input.contactId ? {
        ...contact,
        salutation: clean(input.salutation),
        firstName: clean(input.firstName),
        lastName: clean(input.lastName),
        position: clean(input.position),
        email: clean(input.email),
        phone: clean(input.phone),
        mobile: clean(input.mobile),
        primary,
        active,
      } : contact);
    if (!contacts.some((contact) => contact.active && contact.primary)) {
      const nextPrimary = contacts.find((contact) => contact.active);
      contacts = contacts.map((contact) => contact.id === nextPrimary?.id
        ? { ...contact, primary: true }
        : contact);
    }
    return this.replace(input.tenantId, input.customerId, { ...located.customer, contacts });
  }

  async updateAgreements(input: UpdateCustomerAgreementsInput): Promise<CustomerDetailRecord> {
    const located = this.locate(input.tenantId, input.customerId);
    return this.replace(input.tenantId, input.customerId, {
      ...located.customer,
      agreements: normalizeCustomerAgreements(input.agreements),
    });
  }

  private attachmentKey(tenantId: string, customerId: string): string {
    return `${tenantId}:${customerId}`;
  }

  async listAttachments(tenantId: string, customerId: string): Promise<readonly CustomerAttachmentRecord[]> {
    const list = this.attachments.get(this.attachmentKey(tenantId, customerId)) ?? [];
    // Wie live: Bilder mit Inhalt (Vorschau), Dokumente ohne (erst beim Download nachladen).
    return clone(list.map((item) => (item.kind === 'image' ? item : { ...item, content: '' })))
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
  }

  async getAttachment(tenantId: string, attachmentId: string): Promise<CustomerAttachmentRecord> {
    for (const [key, list] of this.attachments) {
      if (!key.startsWith(`${tenantId}:`)) continue;
      const found = list.find((item) => item.id === attachmentId);
      if (found) return clone(found);
    }
    throw new Error('Der Anhang wurde nicht gefunden.');
  }

  async addAttachment(input: AddCustomerAttachmentInput): Promise<CustomerAttachmentRecord> {
    const record: CustomerAttachmentRecord = {
      id: `${input.customerId}-attachment-${crypto.randomUUID()}`,
      kind: input.kind,
      title: input.title,
      mimeType: input.mimeType,
      byteSize: input.byteSize,
      createdAt: new Date().toISOString(),
      content: input.content,
    };
    const key = this.attachmentKey(input.tenantId, input.customerId);
    const list = this.attachments.get(key) ?? [];
    list.push(record);
    this.attachments.set(key, list);
    return clone(record.kind === 'image' ? record : { ...record, content: '' });
  }

  async removeAttachment(tenantId: string, attachmentId: string): Promise<void> {
    for (const [key, list] of this.attachments) {
      if (!key.startsWith(`${tenantId}:`)) continue;
      const index = list.findIndex((item) => item.id === attachmentId);
      if (index >= 0) { list.splice(index, 1); return; }
    }
  }

  /**
   * Entfernt einen Datensatz -- das Gegenstueck zum Loeschen in der Datenbank.
   * Ohne das meldet der Demo-Modus "geloescht", waehrend der Eintrag stehen bleibt.
   */
  removeCustomer(tenantId: string, customerId: string): void {
    const liste = this.customers.get(tenantId);
    if (!liste) return;
    const stelle = liste.findIndex((eintrag) => eintrag.id === customerId);
    if (stelle >= 0) liste.splice(stelle, 1);
  }
}

export function createDemoCustomersGateway(): DemoCustomersGateway {
  return new DemoCustomersGateway();
}
